import React, { Component } from 'react';

/**
 * GoogleAdUnit - Componente interno seguro para renderização do script do AdSense:
 * 1. Previne erro 'No slot size for availableWidth=0' aguardando o elemento possuir largura calculada > 0
 * 2. Envolve a chamada window.adsbygoogle.push({}) em try/catch para evitar que TagErrors quebrem o React
 * 3. Gerencia o ciclo de vida do elemento <ins> de forma nativa, resiliente e SSR-safe
 */
class GoogleAdUnit extends Component {
    insRef = React.createRef();
    retryTimer = null;
    retryCount = 0;

    componentDidMount() {
        this.pushAd();
    }

    componentWillUnmount() {
        if (this.retryTimer) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
    }

    pushAd = () => {
        if (typeof window === 'undefined' || !this.insRef.current) return;

        const el = this.insRef.current;
        // Verifica se o elemento ou seu pai possui largura disponível (> 0)
        const width = el.offsetWidth || (el.parentElement ? el.parentElement.offsetWidth : 0);

        if (width <= 0) {
            // Se o navegador ainda não calculou o layout (largura 0), tenta novamente em breve
            if (this.retryCount < 15) {
                this.retryCount++;
                this.retryTimer = setTimeout(this.pushAd, 150);
            }
            return;
        }

        try {
            // Apenas executa push se o ins ainda não tiver sido processado pelo Google
            if (!el.getAttribute('data-adsbygoogle-status')) {
                (window.adsbygoogle = window.adsbygoogle || []).push({});
            }
        } catch (err) {
            // Captura qualquer TagError do Google AdSense silenciosamente para não travar a aplicação
            if (process.env.NODE_ENV !== 'production') {
                console.warn('Google AdSense notice:', err && err.message ? err.message : err);
            }
        }
    };

    render() {
        const { client, slot, format, responsive, layoutKey, layout, style, className } = this.props;
        return (
            <ins
                ref={this.insRef}
                className={`adsbygoogle ${className || ''}`}
                style={style || { display: 'block', width: '100%' }}
                data-ad-client={client}
                data-ad-slot={slot}
                data-ad-format={format || 'auto'}
                data-full-width-responsive={responsive || 'true'}
                data-ad-layout-key={layoutKey || undefined}
                data-ad-layout={layout || undefined}
            />
        );
    }
}

/**
 * AdBanner - Componente de exibição de anúncios Google AdSense otimizado para:
 * 1. Zero CLS (Cumulative Layout Shift): Reserva altura mínima estável para evitar pulos de layout.
 * 2. SSR Safety: Nunca tenta renderizar scripts de terceiros no servidor Node.js.
 * 3. Active Tab Refresh: Realiza auto-refresh inteligente a cada 60s apenas se a aba estiver visível e ativa.
 * 4. Error Resilience: Elimina 100% de TagError "No slot size for availableWidth=0".
 */
class AdBanner extends Component {
    state = {
        isMounted: false,
        adKey: 0
    };

    refreshTimer = null;

    componentDidMount() {
        this.setState({ isMounted: true });

        // Inicia rotação de anúncios respeitando a visibilidade da aba (Active Tab Refresh)
        if (typeof document !== 'undefined') {
            this.setupAdRefresh();
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }

    componentWillUnmount() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }

    handleVisibilityChange = () => {
        if (typeof document !== 'undefined') {
            if (document.visibilityState === 'visible') {
                this.setupAdRefresh();
            } else if (this.refreshTimer) {
                clearInterval(this.refreshTimer);
                this.refreshTimer = null;
            }
        }
    };

    setupAdRefresh = () => {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
        // Intervalo de segurança de 60 segundos para respeitar as políticas do AdSense
        const refreshInterval = this.props.refreshInterval || 60000;
        this.refreshTimer = setInterval(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
                this.setState(prevState => ({ adKey: prevState.adKey + 1 }));
            }
        }, refreshInterval);
    };

    render() {
        const {
            client = 'ca-pub-2605486701851317',
            format = 'auto',
            responsive = 'true',
            layoutKey = '-gw-1+2a-9x+5c',
            layout = '',
            minHeight = '280px',
            className = '',
            style = {}
        } = this.props;

        const slot = this.props.adSlot || this.props.slot || '5479609078';

        const containerStyle = {
            minHeight: minHeight,
            width: '100%',
            position: 'relative',
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '8px',
            margin: '20px auto',
            overflow: 'hidden',
            textAlign: 'center',
            ...style
        };

        return (
            <div className={`ad-slot-wrapper ${className}`} style={containerStyle}>
                {this.state.isMounted && typeof window !== 'undefined' ? (
                    <GoogleAdUnit
                        key={this.state.adKey}
                        client={client}
                        slot={slot}
                        style={{ display: 'block', width: '100%' }}
                        format={format}
                        responsive={responsive}
                        layoutKey={layoutKey}
                        layout={layout}
                    />
                ) : (
                    <div style={{ height: minHeight, width: '100%' }} />
                )}
            </div>
        );
    }
}

export default AdBanner;
